import pick from 'lodash/pick';
import { stringify } from 'query-string';
import { getErrorFromResponse } from '@atlassian/jira-common-directory-v2/src/rest/errors.tsx';
import { fetchWithRetry } from '@atlassian/jira-common-directory-v2/src/rest/index.tsx';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import { getDefaultOptions } from '@atlassian/jira-fetch/src/utils/fetch-default-options.tsx';
import type { PageRequest } from '@atlassian/jira-shared-types/src/rest/jira/page.tsx';
import type { Filter } from '../../../model/index.tsx';
import type { Response } from './types.tsx';

const createQueryFilter = (
	filter: Filter,
): {
	filterName: string | undefined;
	isSubstringMatch: boolean;
} => ({
	filterName: filter.query,
	isSubstringMatch: true,
});

const createProjectFilter = (
	filter: Filter,
): {
	projectId: string | undefined;
} => ({
	projectId: filter.project ? filter.project.value : undefined,
});

const createGroupFilter = (
	filter: Filter,
): {
	groupname: string | undefined;
} => ({
	groupname: filter.group ? filter.group.value : undefined,
});

const createOwnerFilter = (
	filter: Filter,
): {
	accountId: string | undefined;
	owner: string | undefined;
} => ({
	accountId: filter.owner ? filter.owner.accountId : undefined,
	owner: undefined,
});

const logError = async (error: Error, currentTry: number) => {
	log.safeErrorWithoutCustomerData(
		'ops.rest.filters.get',
		`Cannot fetch filters - is SSR: ${String(__SERVER__)}, attempt: ${currentTry}`,
		error,
	);
};

export const createEndpointPath = () => '/rest/api/2/filter/search';

export const createUrl = (request: PageRequest, filter: Filter, isAdmin: boolean) => {
	const queryParams = {
		...pick(request, 'startAt', 'maxResults', 'orderBy', 'expand'),
		...(filter.query ? createQueryFilter(filter) : undefined),
		...(filter.project ? createProjectFilter(filter) : undefined),
		...(filter.group ? createGroupFilter(filter) : undefined),
		...(filter.owner ? createOwnerFilter(filter) : undefined),
	};
	return `${createEndpointPath()}?${stringify(queryParams)}${
		isAdmin ? '&overrideSharePermissions=true' : ''
	}`;
};

const getFilters = async (
	request: PageRequest,
	filter: Filter,
	isAdmin: boolean,
): Promise<Response> => {
	const url = createUrl(request, filter, isAdmin);
	const response = await fetchWithRetry(url, getDefaultOptions(url), logError);

	if (response.ok) {
		return response.json();
	}

	throw await getErrorFromResponse(response, 'Cannot fetch filters');
};

export default getFilters;
